// src/lib/modelCatalog.ts
import type { OpenAIParams } from '@/types';

export type ProviderName = 'gemini' | 'openai';

/**
 * Whether a model produces images or text. Used to constrain the admin
 * model-picker so an image-generation step (e.g. staged_room, refine_global)
 * can only be assigned an image model, and a text step (a classifier) can only
 * be assigned a text model — see STEP_MODALITY + modelsForStep below.
 */
export type ModelKind = 'image' | 'text';

export interface ModelEntry {
  id: string;
  label: string;
  kind: ModelKind;
}

export const MODEL_CATALOG: Record<ProviderName, ModelEntry[]> = {
  gemini: [
    { id: 'gemini-3.1-flash-lite',  label: 'Gemini 3.1 Flash Lite (text, classifier)', kind: 'text' },
    { id: 'gemini-3.7-flash',       label: 'Gemini 3.7 Flash (text, smart)',           kind: 'text' },
    { id: 'gemini-3.6-flash',       label: 'Gemini 3.6 Flash (text, legacy)',          kind: 'text' },
    { id: 'gemini-3.1-flash-image', label: 'Gemini 3.1 Flash Image (Nano Banana 2)',   kind: 'image' },
    { id: 'gemini-3-pro-image',     label: 'Gemini 3 Pro Image (Nano Banana Pro)',     kind: 'image' },
  ],
  openai: [
    { id: 'gpt-image-2',            label: 'GPT Image 2 (latest)',                     kind: 'image' },
    { id: 'gpt-image-2-2026-04-21', label: 'GPT Image 2 (2026-04-21 snapshot)',        kind: 'image' },
  ],
};

/**
 * Required model kind per step. Steps not listed default to 'image' (the common
 * case — every generation/edit step produces an image). Only classifier steps,
 * which reason over the prompt text and emit JSON, require a text model.
 */
export const STEP_MODALITY: Record<string, ModelKind> = {
  classifier: 'text',            // floorplan edit-classifier
  reprompt_classifier: 'text',   // staging reprompt-classifier (Stage 1)
  reprompt_rewrite: 'text',      // staging reprompt-rewrite (Stage 2)
  consult: 'text',                // catalog-staging consult step
};

export const stepModality = (step: string): ModelKind => STEP_MODALITY[step] ?? 'image';

/** Models available for a step, filtered to the step's required kind. */
export const modelsForStep = (provider: ProviderName, step: string): ModelEntry[] =>
  MODEL_CATALOG[provider].filter(m => m.kind === stepModality(step));

/** Providers that offer at least one model of the step's required kind. */
export const providersForStep = (step: string): ProviderName[] =>
  (Object.keys(MODEL_CATALOG) as ProviderName[]).filter(
    p => MODEL_CATALOG[p].some(m => m.kind === stepModality(step)),
  );

export const PROVIDER_LABELS: Record<ProviderName, string> = {
  gemini: 'Google Gemini',
  openai: 'OpenAI',
};

export const STEP_LABELS_DE: Record<string, string> = {
  '2d_generation': '2D-Grundriss generieren',
  '3d_generation': '3D-Dollhouse generieren',
  'empty_room':    'Raum leeren',
  'staged_room':   'Raum einrichten',
  'refinement':    'Grundriss bearbeiten',
  'classifier':    'Bearbeitungs-Klassifizierer',
  'reprompt_classifier': 'Reprompt-Klassifizierer',
  'reprompt_rewrite': 'Reprompt-Rewrite',
  'refine_local':  'Reprompt (lokal, Maske)',
  'refine_global': 'Reprompt (global)',
  'refine_v2':     'Reprompting v2 (Nano Banana)',
  'consult':       'Berater (Katalog)',
};
// 'unit_extraction' intentionally omitted — client-side only, no model to choose.

export const OPENAI_QUALITY_OPTIONS: NonNullable<OpenAIParams['quality']>[] = [
  'low', 'medium', 'high', 'auto',
];
